import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { canAccessCommercialMap } from "./appChromeAccess";
import MapView from "@arcgis/core/views/MapView.js";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import KMLLayer from "@arcgis/core/layers/KMLLayer.js";
import Graphic from "@arcgis/core/Graphic.js";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import PopupTemplate from "@arcgis/core/PopupTemplate.js";
import Search from "@arcgis/core/widgets/Search.js";
import LocatorSearchSource from "@arcgis/core/widgets/Search/LocatorSearchSource.js";
import {
  configureArcgisApiKey,
  GEOCODE_URL,
  getArcgisApiKey,
  getArcgisSegmentsKmzUrl,
  hexToEsriRgba,
  loadMapForPreview,
  loadResource,
  resolveServiceLocationPoint,
  VIEW_LOAD_MS,
  withTimeout,
} from "./arcgisMapUtils";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { isCommercialProjectRow, readCommercialDomainsForRow } from "./commercialProjectAcf";
import { isNonRevenueOrCapitalProject } from "./commercialSubphases";
import {
  displayPhaseForRow,
  GLOBAL_PHASES,
  PHASE_CELL_BACKGROUND,
  resolvePhaseStyleKey,
} from "../projectPhaseDisplay";
import { isProjectFieldFileContentUrl } from "../projectFieldFileAuth";
import { normalizeProjectSearchQuery } from "./boardProjectSearch";
import "./commercialLocationsMap.css";

const AUTH_TOKEN_KEY = "projectBoardAuthToken";

/** Cached short-lived asset-read JWT for Esri KML fetches (never the session token). */
let assetReadTokenCache = { token: "", expiresAtMs: 0 };

async function fetchAssetReadToken(apiUrl, getAuthHeaders) {
  const now = Date.now();
  if (assetReadTokenCache.token && now < assetReadTokenCache.expiresAtMs - 60_000) {
    return assetReadTokenCache.token;
  }
  const { data } = await axios.post(
    `${String(apiUrl || "").replace(/\/$/, "")}/map-assets/token`,
    {},
    { headers: getAuthHeaders?.() || {} }
  );
  const token = String(data?.token || "").trim();
  if (!token) throw new Error("Could not obtain a map asset token.");
  const ttlSec = Number(data?.expiresInSec) || 15 * 60;
  assetReadTokenCache = { token, expiresAtMs: now + ttlSec * 1000 };
  return token;
}

const GEOCODE_CONCURRENCY = 4;
const EXCLUDED_MAP_STATUSES = new Set(["complete", "cancelled"]);
const DEFAULT_MARKER_COLOR = hexToEsriRgba("#64748b");
const MARKERS_LAYER_ID = "pb-commercial-locations";
const SEGMENTS_LAYER_ID = "pb-segments-kmz";
const POPUP_ACTION_OPEN_PROJECT = "open-project";

function isAppOverlayLayer(layer) {
  const id = String(layer?.id || "");
  return id === MARKERS_LAYER_ID || id === SEGMENTS_LAYER_ID;
}

function collectNetworkMapLayers(map) {
  if (!map?.layers) return [];
  return map.layers.toArray().filter((ly) => !isAppOverlayLayer(ly));
}

function projectStatusForRow(row) {
  const domains = readCommercialDomainsForRow(row, row.acf_domains);
  const fromSummary = domains?.[COMMERCIAL_DOMAINS.SUMMARY]?.status;
  if (fromSummary != null && String(fromSummary).trim() !== "") {
    return String(fromSummary).trim();
  }
  return row?.status != null ? String(row.status).trim() : "";
}

function isExcludedMapStatus(status) {
  return EXCLUDED_MAP_STATUSES.has(String(status || "").trim().toLowerCase());
}

function phaseMarkerColor(phaseLabel) {
  const key = resolvePhaseStyleKey(phaseLabel);
  const hex = key ? PHASE_CELL_BACKGROUND[key] : null;
  return hex ? hexToEsriRgba(hex) : DEFAULT_MARKER_COLOR;
}

function serviceLocationForRow(row) {
  const col = row?.service_location;
  if (col != null && String(col).trim() !== "") return String(col).trim();
  const domains = readCommercialDomainsForRow(row, row.acf_domains);
  const fromSummary = domains?.[COMMERCIAL_DOMAINS.SUMMARY]?.service_location;
  return fromSummary != null ? String(fromSummary).trim() : "";
}

function projectSummaryForTypeCheck(row) {
  const domains = readCommercialDomainsForRow(row, row.acf_domains);
  const summary = { ...(domains?.[COMMERCIAL_DOMAINS.SUMMARY] || {}) };
  if ((!summary.type || String(summary.type).trim() === "") && row?.project_type != null) {
    summary.type = String(row.project_type).trim();
  }
  return summary;
}

function entryMatchesMapSearch(entry, query) {
  const q = normalizeProjectSearchQuery(query);
  if (!q || !entry) return true;
  const haystacks = [entry.workOrder, entry.name, entry.serviceLocation, entry.phase, entry.status];
  return haystacks.some((value) => String(value || "").toLowerCase().includes(q));
}

function buildCommercialMapEntries(rows) {
  const list = [];
  for (const row of rows || []) {
    if (!isCommercialProjectRow(row)) continue;
    if (isNonRevenueOrCapitalProject(projectSummaryForTypeCheck(row))) continue;
    const status = projectStatusForRow(row);
    if (isExcludedMapStatus(status)) continue;
    const serviceLocation = serviceLocationForRow(row);
    if (!serviceLocation) continue;
    list.push({
      id: row.id,
      workOrder: row.work_order != null ? String(row.work_order).trim() : "",
      name: row.name != null ? String(row.name).trim() : "",
      status,
      phase: displayPhaseForRow(row) || "Unknown",
      serviceLocation,
    });
  }
  return list;
}

async function mapPool(items, concurrency, worker) {
  const results = [];
  let index = 0;
  async function run() {
    while (index < items.length) {
      const i = index;
      index += 1;
      results[i] = await worker(items[i], i);
    }
  }
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, () => run());
  await Promise.all(runners);
  return results;
}

function kmzNeedsAuthenticatedFetch(url) {
  const s = String(url || "");
  return isProjectFieldFileContentUrl(s) || /\/map-assets\/segments\.kmz(?:\?|#|$)/i.test(s);
}

/**
 * KMLLayer is parsed by Esri's utility service — it needs a public HTTPS URL, not a blob URL.
 * Uses a short-lived asset-read token in the query string (not the session JWT).
 */
async function kmlLayerFetchUrl(url, { apiUrl, getAuthHeaders }) {
  const s = String(url || "").trim();
  if (!kmzNeedsAuthenticatedFetch(s)) return s;
  const sessionPresent =
    typeof localStorage !== "undefined" && Boolean(localStorage.getItem(AUTH_TOKEN_KEY));
  if (!sessionPresent) {
    throw new Error("Sign in required to load Segments KMZ.");
  }
  const token = await fetchAssetReadToken(apiUrl, getAuthHeaders);
  const absolute = /^https?:\/\//i.test(s)
    ? s
    : new URL(s, typeof window !== "undefined" ? window.location.origin : undefined).href;
  const u = new URL(absolute);
  u.searchParams.set("access_token", token);
  return u.href;
}

async function createKmlLayerFromUrl(url, authCtx) {
  const layer = new KMLLayer({ url: await kmlLayerFetchUrl(url, authCtx) });
  await loadResource(layer);
  return { layer };
}

function popupContent(entry) {
  const title = [entry.workOrder, entry.name].filter(Boolean).join(" — ") || `Project #${entry.id}`;
  const lines = [];
  if (entry.phase) {
    lines.push(`<p><strong>Phase:</strong> ${escapeHtml(entry.phase)}</p>`);
  }
  if (entry.status) {
    lines.push(`<p><strong>Status:</strong> ${escapeHtml(entry.status)}</p>`);
  }
  if (entry.serviceLocation) {
    lines.push(`<p><strong>Service location:</strong> ${escapeHtml(entry.serviceLocation)}</p>`);
  }
  return { title, content: lines.join("") };
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function projectLandingUrl(projectId) {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  return `${origin}/project/${encodeURIComponent(String(projectId))}`;
}

function CommercialLocationsMapPage({ apiUrl, getAuthHeaders }) {
  const apiKey = getArcgisApiKey();
  const segmentsKmzUrl = useMemo(() => getArcgisSegmentsKmzUrl(apiUrl), [apiUrl]);
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const markersLayerRef = useRef(null);
  const networkLayersRef = useRef([]);
  const popupActionHandleRef = useRef(null);
  const searchWidgetRef = useRef(null);

  const [phase, setPhase] = useState("loading");
  const [statusHint, setStatusHint] = useState("Loading projects…");
  const [errorMessage, setErrorMessage] = useState("");
  const [segmentsError, setSegmentsError] = useState("");
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, plotted: 0, segmentsLoaded: false });
  const [showServiceLocations, setShowServiceLocations] = useState(true);
  const [showNetworkMap, setShowNetworkMap] = useState(true);
  const [showSegments, setShowSegments] = useState(true);
  const [mapBusy, setMapBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [geocodedHits, setGeocodedHits] = useState([]);

  const filteredGeocodedHits = useMemo(() => {
    if (!normalizeProjectSearchQuery(searchQuery)) return geocodedHits;
    return geocodedHits.filter((hit) => entryMatchesMapSearch(hit.entry, searchQuery));
  }, [geocodedHits, searchQuery]);

  const searchActive = normalizeProjectSearchQuery(searchQuery).length > 0;

  useEffect(() => {
    let cancelled = false;
    setStatusHint("Loading projects…");
    axios
      .get(`${apiUrl}/projects`, { headers: getAuthHeaders() })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const built = buildCommercialMapEntries(rows);
        setEntries(built);
        setStats({ total: built.length, plotted: 0, segmentsLoaded: false });
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorMessage(err.response?.data?.error || err.message || "Could not load projects.");
          setPhase("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, getAuthHeaders]);

  const removeSegmentsLayer = useCallback((map) => {
    if (!map) return;
    const existing = map.findLayerById(SEGMENTS_LAYER_ID);
    if (existing) map.remove(existing);
  }, []);

  const loadSegmentsLayer = useCallback(
    async (view, isCancelled) => {
      if (!view?.map || !segmentsKmzUrl || !showSegments) {
        removeSegmentsLayer(view?.map);
        setStats((s) => ({ ...s, segmentsLoaded: false }));
        return;
      }
      removeSegmentsLayer(view.map);
      setSegmentsError("");
      try {
        const { layer } = await createKmlLayerFromUrl(segmentsKmzUrl, {
          apiUrl,
          getAuthHeaders,
        });
        if (isCancelled()) return;
        layer.id = SEGMENTS_LAYER_ID;
        layer.title = "Segments";
        layer.opacity = 0.75;
        view.map.add(layer, 0);
        setStats((s) => ({ ...s, segmentsLoaded: true }));
      } catch (err) {
        if (!isCancelled()) {
          setSegmentsError(err?.message || "Could not load Segments KMZ.");
          setStats((s) => ({ ...s, segmentsLoaded: false }));
        }
      }
    },
    [apiUrl, getAuthHeaders, segmentsKmzUrl, showSegments, removeSegmentsLayer]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!apiKey) {
      setPhase("error");
      setErrorMessage(
        "Embedded map is disabled: set REACT_APP_ARCGIS_API_KEY when building the frontend (same key as project landing maps)."
      );
      return undefined;
    }
    if (!el) return undefined;

    let cancelled = false;
    const isCancelled = () => cancelled;
    configureArcgisApiKey(apiKey);

    (async () => {
      setPhase("loading");
      setStatusHint("Initializing map…");
      setErrorMessage("");
      try {
        const map = await loadMapForPreview(apiKey, { requireWebMap: true });
        if (isCancelled()) return;

        const markersLayer = new GraphicsLayer({ id: MARKERS_LAYER_ID });
        map.add(markersLayer);
        markersLayerRef.current = markersLayer;
        networkLayersRef.current = collectNetworkMapLayers(map);

        const view = new MapView({
          container: el,
          map,
        });
        viewRef.current = view;

        await withTimeout(loadResource(view), VIEW_LOAD_MS, "Map view");
        if (typeof view.setupPopup === "function") {
          try {
            await view.setupPopup();
          } catch {
            /* popup may still attach on first map click */
          }
        }
        if (isCancelled()) return;

        const searchWidget = new Search({
          view,
          includeDefaultSources: false,
          sources: [
            new LocatorSearchSource({
              url: GEOCODE_URL,
              apiKey,
              singleLineFieldName: "SingleLine",
              name: "World Geocoding Service",
              placeholder: "Search address or coordinates",
            }),
          ],
          allPlaceholder: "Search address or coordinates",
        });
        view.ui.add(searchWidget, { position: "top-right" });
        searchWidgetRef.current = searchWidget;

        const attachPopupAction = () => {
          const popupWidget = view.popup;
          if (!popupWidget || typeof popupWidget.on !== "function") return null;
          return popupWidget.on("trigger-action", (event) => {
            if (event.action.id !== POPUP_ACTION_OPEN_PROJECT) return;
            const projectId = view.popup.selectedFeature?.attributes?.projectId;
            if (projectId == null) return;
            window.open(projectLandingUrl(projectId), "_blank", "noopener,noreferrer");
          });
        };

        popupActionHandleRef.current = attachPopupAction();
        if (!popupActionHandleRef.current && typeof view.setupPopup === "function") {
          try {
            await view.setupPopup();
            if (!isCancelled()) popupActionHandleRef.current = attachPopupAction();
          } catch {
            /* ignore */
          }
        }

        if (map.initialViewProperties?.viewpoint && !isCancelled()) {
          try {
            await view.goTo(map.initialViewProperties.viewpoint);
          } catch {
            /* use map default */
          }
        }
        if (isCancelled()) return;

        setStatusHint("Loading Segments…");
        await loadSegmentsLayer(view, isCancelled);
        if (isCancelled()) return;
        setPhase("ready");
      } catch (e) {
        if (!isCancelled()) {
          setPhase("error");
          setErrorMessage(e?.message || "Could not initialize the map.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (popupActionHandleRef.current) {
        try {
          popupActionHandleRef.current.remove();
        } catch {
          /* ignore */
        }
        popupActionHandleRef.current = null;
      }
      if (searchWidgetRef.current) {
        try {
          searchWidgetRef.current.destroy();
        } catch {
          /* ignore */
        }
        searchWidgetRef.current = null;
      }
      removeSegmentsLayer(viewRef.current?.map);
      if (viewRef.current) {
        try {
          viewRef.current.destroy();
        } catch {
          /* ignore */
        }
        viewRef.current = null;
      }
      markersLayerRef.current = null;
      networkLayersRef.current = [];
    };
  }, [apiKey, loadSegmentsLayer, removeSegmentsLayer]);

  useEffect(() => {
    const markersLayer = markersLayerRef.current;
    if (!markersLayer || phase !== "ready") return;
    markersLayer.visible = showServiceLocations;
    if (!showServiceLocations && viewRef.current?.popup?.visible) {
      viewRef.current.popup.close();
    }
  }, [showServiceLocations, phase]);

  useEffect(() => {
    if (phase !== "ready") return;
    for (const layer of networkLayersRef.current) {
      layer.visible = showNetworkMap;
    }
  }, [showNetworkMap, phase]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || phase !== "ready") return undefined;
    let cancelled = false;
    setStatusHint("Loading Segments…");
    loadSegmentsLayer(view, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [phase, showSegments, loadSegmentsLayer]);

  const createMarkerGraphic = useCallback((entry, point) => {
    const popup = popupContent(entry);
    return new Graphic({
      geometry: point,
      attributes: { projectId: entry.id, phase: entry.phase },
      symbol: new SimpleMarkerSymbol({
        color: phaseMarkerColor(entry.phase),
        outline: { color: [255, 255, 255], width: 2 },
        size: 11,
      }),
      popupTemplate: new PopupTemplate({
        title: popup.title,
        content: popup.content,
        actions: [
          {
            id: POPUP_ACTION_OPEN_PROJECT,
            title: "Open project",
            type: "button",
          },
        ],
      }),
    });
  }, []);

  useEffect(() => {
    if (phase !== "ready" || !apiKey) return undefined;

    let cancelled = false;
    const isCancelled = () => cancelled;

    (async () => {
      setMapBusy(true);
      setGeocodedHits([]);
      setStats((s) => ({ ...s, plotted: 0 }));

      if (entries.length === 0) {
        setStatusHint("No active commercial projects with a service location.");
        setMapBusy(false);
        return;
      }

      setStatusHint(`Locating ${entries.length} service location(s)…`);

      const geocoded = await mapPool(entries, GEOCODE_CONCURRENCY, async (entry) => {
        if (isCancelled()) return null;
        try {
          const point = await resolveServiceLocationPoint(entry.serviceLocation, isCancelled, apiKey);
          return point ? { entry, point } : null;
        } catch {
          return null;
        }
      });

      if (isCancelled()) return;

      const hits = geocoded.filter((hit) => hit?.point);
      setGeocodedHits(hits);
      setStats((s) => ({ ...s, plotted: hits.length }));
      setStatusHint(
        hits.length > 0
          ? `${hits.length} location(s) on map`
          : "No locations could be geocoded."
      );
      setMapBusy(false);
    })();

    return () => {
      cancelled = true;
      setMapBusy(false);
    };
  }, [entries, phase, apiKey]);

  useEffect(() => {
    const view = viewRef.current;
    const markersLayer = markersLayerRef.current;
    if (!view || !markersLayer || phase !== "ready") return undefined;

    let cancelled = false;
    markersLayer.removeAll();

    const graphics = filteredGeocodedHits.map((hit) => createMarkerGraphic(hit.entry, hit.point));
    if (graphics.length > 0) {
      markersLayer.addMany(graphics);
    }
    markersLayer.visible = showServiceLocations;

    if (!cancelled && graphics.length > 0 && showServiceLocations) {
      (async () => {
        try {
          await view.goTo(graphics, { padding: 48 });
        } catch {
          /* ignore extent errors */
        }
      })();
    }

    if (!cancelled) {
      if (searchActive) {
        setStatusHint(
          graphics.length > 0
            ? `${graphics.length} matching location(s) on map`
            : "No projects match your search."
        );
      } else if (geocodedHits.length > 0) {
        setStatusHint(`${geocodedHits.length} location(s) on map`);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [
    filteredGeocodedHits,
    geocodedHits,
    createMarkerGraphic,
    phase,
    showServiceLocations,
    searchActive,
  ]);

  if (phase === "error" && errorMessage) {
    return (
      <div className="commercial-locations-page">
        <div className="commercial-locations-header">
          <h2>Commercial project map</h2>
        </div>
        <div className="commercial-locations-map-error" role="alert">
          {errorMessage}
        </div>
        <p className="project-detail-muted" style={{ marginTop: 12 }}>
          <Link to="/">← Back to board</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="commercial-locations-page">
      <header className="commercial-locations-header">
        <h2>Commercial project map</h2>
        <p className="commercial-locations-lead">
          Active commercial revenue projects (excluding Complete, Cancelled, Capital, and Non-Revenue) by service
          location, colored by phase. Uses the same ArcGIS web map as project landing pages — toggle{" "}
          <strong>Network map</strong> for its feature layers (fiber network). The <strong>Segments</strong> layer
          loads <code>Primary Segments.kmz</code> from the API (
          <code>PROJECT_BOARD_SEGMENTS_KMZ_PATH</code> or <code>backend/static/map/Primary-Segments.kmz</code>).
        </p>
        <div className="commercial-locations-search">
          <input
            type="search"
            className="commercial-locations-search-input"
            placeholder="Search work order, name, or location…"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            aria-label="Search projects on map"
          />
        </div>
        <div className="commercial-locations-toolbar">
          <span className="commercial-locations-stat">
            {searchActive ? (
              <>
                <strong>{filteredGeocodedHits.length}</strong> of <strong>{stats.total}</strong> shown
              </>
            ) : (
              <>
                <strong>{stats.total}</strong> project{stats.total === 1 ? "" : "s"} with location
              </>
            )}
          </span>
          <span className="commercial-locations-stat">
            <strong>{searchActive ? filteredGeocodedHits.length : stats.plotted}</strong> plotted
          </span>
          <span className="commercial-locations-stat">
            Segments: <strong>{stats.segmentsLoaded ? "loaded" : "not loaded"}</strong>
          </span>
          <label className="commercial-locations-toggle">
            <input
              type="checkbox"
              checked={showNetworkMap}
              onChange={(e) => setShowNetworkMap(e.target.checked)}
            />
            Network map
          </label>
          <label className="commercial-locations-toggle">
            <input
              type="checkbox"
              checked={showServiceLocations}
              onChange={(e) => setShowServiceLocations(e.target.checked)}
            />
            Service locations
          </label>
          <label className="commercial-locations-toggle">
            <input type="checkbox" checked={showSegments} onChange={(e) => setShowSegments(e.target.checked)} />
            Segments
          </label>
        </div>
        {segmentsError ? (
          <p className="commercial-locations-segments-error" role="alert">
            {segmentsError}
          </p>
        ) : null}
      </header>

      <div className="commercial-locations-map-stage">
        <div ref={containerRef} className="commercial-locations-map-container" />
        {phase === "loading" || mapBusy ? (
          <div className="commercial-locations-map-overlay" aria-live="polite">
            {statusHint}
          </div>
        ) : null}
      </div>

      <div className="commercial-locations-legend commercial-locations-legend--phases">
        {GLOBAL_PHASES.map((phaseName) => (
          <span key={phaseName} className="commercial-locations-legend-item">
            <span
              className="commercial-locations-legend-swatch"
              style={{ background: PHASE_CELL_BACKGROUND[phaseName] }}
              aria-hidden
            />
            {phaseName}
          </span>
        ))}
        <span className="commercial-locations-legend-item">
          <span
            className="commercial-locations-legend-swatch commercial-locations-legend-swatch--segment"
            aria-hidden
          />
          Segments
        </span>
      </div>
    </div>
  );
}

export function CommercialLocationsMapGate({ apiUrl, getAuthHeaders }) {
  if (!canAccessCommercialMap()) {
    return <Navigate to="/" replace />;
  }
  return <CommercialLocationsMapPage apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />;
}

export default CommercialLocationsMapPage;
